import { useCallback, useState } from 'react';
import JSZip from 'jszip';
import { ResolutionConfig } from '../../shared/types/binding-types';
import { CanvasJSON } from '../../canvas/types';
import { renderResolutionsToBlobs } from '../utils/canvas-image';

type UseExportAllImagesArgs = {
  resolutions: ResolutionConfig[];
  resolveResolutionJson: (resolution: ResolutionConfig) => Promise<CanvasJSON>;
};

interface ExportAllOptions {
  getFileName: (resolution: ResolutionConfig) => string;
  zipFileName?: string;
}

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

export function useExportAllImages({ resolutions, resolveResolutionJson }: UseExportAllImagesArgs) {
  const [isExportingAll, setIsExportingAll] = useState(false);

  const exportAll = useCallback(
    async ({ getFileName, zipFileName }: ExportAllOptions) => {
      if (isExportingAll || resolutions.length === 0) return;

      setIsExportingAll(true);

      try {
        const rendered = await renderResolutionsToBlobs(resolutions, resolveResolutionJson);

        const link = document.createElement('a');

        if (rendered.length === 1) {
          const { resolution, blob } = rendered[0];
          link.href = URL.createObjectURL(blob);
          link.download = getFileName(resolution);
        } else {
          const zip = new JSZip();
          for (const { resolution, blob } of rendered) {
            const base64 = await blobToBase64(blob);
            zip.file(getFileName(resolution), base64, { base64: true });
          }
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          link.href = URL.createObjectURL(zipBlob);
          link.download = zipFileName ?? `export-${Date.now()}.zip`;
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } finally {
        setIsExportingAll(false);
      }
    },
    [resolutions, resolveResolutionJson, isExportingAll]
  );

  return { exportAll, isExportingAll };
}
